/**
 * Structured hub templates.
 *
 * In `structured` layout mode the RENDERER owns section placement. Each templated
 * hub kind declares a fixed, ordered list of slots, and every slot is pinned to
 * exactly one component type. The generator still authors the content for each
 * slot with full page context, but it does not choose the section set or order:
 * the renderer projects whatever the generator emits onto this template (see
 * `StructuredHubLayout`). Canvas mode is unaffected; there the LLM owns order.
 *
 * This module is the single source of truth shared by:
 *   - the generator blueprint prompt (`apps/api/.../hubPageStagedGeneration.ts`)
 *   - the renderer projection (`apps/web/.../layouts/StructuredHubLayout.tsx`)
 *   - the publish-completeness gate (`apps/api/src/lib/research-hubs/service.ts`)
 *
 * Kinds NOT present in `HUB_STRUCTURED_TEMPLATES` fall back to the legacy
 * generator-ordered contract.
 *
 * Informational pages are report-first; transactional pages are product-first.
 * Report-card components (reportGrid, anchorLinkList) are cut from the
 * transactional palette entirely, so transactional surfaces reports only as
 * evidence (pick provenance, methodology, the sources count), never as
 * browsable content. The sibling band already bridges to the informational hub.
 */

import type { HubKind, HubSectionType } from './schema'

export interface HubStructuredSlot {
  /** Stable slot identifier. Also used as the rendered section id unless `anchorId` is set. */
  slotId: string
  /** The single component type this slot always renders. */
  sectionType: HubSectionType
  /** Designer-facing role for the slot, surfaced verbatim in the blueprint prompt. */
  role: string
  /** Fixed anchor id applied to the rendered section (e.g. sources => "sources"). */
  anchorId?: string
}

const INFORMATIONAL_TEMPLATE: readonly HubStructuredSlot[] = [
  {
    slotId: 'hero',
    sectionType: 'hero',
    role: 'Page H1, kicker, and trust meta line.',
  },
  {
    slotId: 'intro',
    sectionType: 'intro',
    role: 'Editorial framing of the reader problem; link inline into reports.',
  },
  {
    slotId: 'anchor-link-list',
    sectionType: 'anchorLinkList',
    role: 'One carousel card per assigned report.',
  },
  {
    slotId: 'guide-steps',
    sectionType: 'guideSteps',
    role: 'Ordered how-to or decision steps.',
  },
  {
    slotId: 'guide-pathways',
    sectionType: 'guidePathways',
    role: 'Reader situations or use cases mapped to one report each.',
  },
  {
    slotId: 'concern-links',
    sectionType: 'concernLinks',
    role: 'Concern or persona chips that route to report anchors.',
  },
  {
    slotId: 'pull-quote',
    sectionType: 'pullQuote',
    role: 'One high-leverage sourced quote.',
  },
  {
    slotId: 'faq',
    sectionType: 'faq',
    role: 'Reader follow-up questions with concise answers.',
  },
  {
    slotId: 'sources-summary',
    sectionType: 'sourcesSummary',
    role: 'Evidence summary; always closes the page.',
    anchorId: 'sources',
  },
]

const TRANSACTIONAL_TEMPLATE: readonly HubStructuredSlot[] = [
  {
    slotId: 'hero',
    sectionType: 'hero',
    role: 'Page H1, kicker, and trust meta line.',
  },
  {
    slotId: 'intro',
    sectionType: 'intro',
    role: 'Editorial framing of the buying decision and what separates the picks.',
  },
  {
    slotId: 'top-picks',
    sectionType: 'topPicks',
    role: 'The product picks with role labels and decisive comparative reasons.',
  },
  {
    slotId: 'comparison-table',
    sectionType: 'comparisonTable',
    role: 'Side-by-side comparison matrix across the picks.',
  },
  {
    slotId: 'product-rail',
    sectionType: 'productRail',
    role: 'Runner-up and alternative products beyond the top picks.',
  },
  {
    slotId: 'concern-links',
    sectionType: 'concernLinks',
    role: 'Concern chips that route the reader to the right pick.',
  },
  {
    slotId: 'decision-block',
    sectionType: 'decisionBlock',
    role: 'Decision callout pointing to the comparison or the right pick.',
  },
  {
    slotId: 'methodology-card',
    sectionType: 'methodologyCard',
    role: 'How the picks were evaluated; credibility of the method.',
  },
  {
    slotId: 'pull-quote',
    sectionType: 'pullQuote',
    role: 'One high-leverage sourced quote.',
  },
  {
    slotId: 'faq',
    sectionType: 'faq',
    role: 'Buyer questions with concise answers.',
  },
  {
    slotId: 'cta-band',
    sectionType: 'ctaBand',
    role: 'Final call to action toward the strongest pick.',
  },
  {
    slotId: 'sources-summary',
    sectionType: 'sourcesSummary',
    role: 'Evidence summary; always closes the page.',
    anchorId: 'sources',
  },
]

/**
 * Templated hub kinds. A kind that is absent here renders via the legacy
 * generator-ordered contract in `StructuredHubLayout`.
 */
export const HUB_STRUCTURED_TEMPLATES: Partial<
  Record<HubKind, readonly HubStructuredSlot[]>
> = {
  informational: INFORMATIONAL_TEMPLATE,
  transactional: TRANSACTIONAL_TEMPLATE,
}

export function getHubStructuredTemplate(
  hubKind: HubKind
): readonly HubStructuredSlot[] | undefined {
  return HUB_STRUCTURED_TEMPLATES[hubKind]
}

/** Ordered section types for a templated kind, or `undefined` for legacy kinds. */
export function getHubStructuredTemplateTypes(
  hubKind: HubKind
): readonly HubSectionType[] | undefined {
  return getHubStructuredTemplate(hubKind)?.map((slot) => slot.sectionType)
}
